// Generated from CryptoBridge.ts by the TypeScript compiler 5.9.3
// (tsc --target ES2017 --module ES2015 --moduleResolution node --strict). Only the type annotations are removed,
// so the logic is identical to CryptoBridge.ts. Do not edit this file on its own: change
// CryptoBridge.ts and regenerate this file in the same commit, so the two stay identical.
// For JavaScript projects without TypeScript (such as the hz-uae web apps). Checked with node-forge 1.3.1 and 1.3.3.
/**
 * Partner-side AMOS crypto for the client side.
 *
 * Where it runs: in the browser, the WebView, the mobile app (React Native, Capacitor) and the web app.
 *
 * Key: it uses the AMOS public key only (Base64 X.509 SubjectPublicKeyInfo DER).
 *   - encryptData: encrypts a request payload with the AMOS public key (RSAES-PKCS1-v1_5, block type 2), exactly as
 *     Java's Cipher "RSA" ENCRYPT_MODE with a public key.
 *   - decryptData: decrypts a response payload with the AMOS public key (raw RSA, unwrapping PKCS#1 block type 1),
 *     exactly as Java's Cipher "RSA" DECRYPT_MODE with a public key.
 *
 * It contains no secret key and no PUID logic, and no private-key code. Never put a secret key or a private key in
 * this file or in any browser, WebView or app code. Secret-key work (today's AES format and the partner user ID
 * (PUID) in AES-GCM) is done on the partner's server only, with CryptoBridgePartner.java.
 *
 * Dependency: node-forge 1.3.3 (as in amos-app; types @types/node-forge 1.3.14). It does not use Node's crypto
 * module or Buffer, so it runs in browsers and mobile JavaScript engines (WebCrypto has neither PKCS#1 v1.5
 * encryption nor public-key decryption).
 *
 * Hex in and out, UTF-8 text, RSA block sizes taken from the loaded key's own modulus (any key size).
 */
import * as forge from "node-forge";
const SECURITY_KEY_TYPE_PUBLIC = "PUBLIC_KEY";
// Equivalent of org.springframework.http.HttpStatus.PRECONDITION_FAILED.toString()
const EXCEPTION_MESSAGE_PREFIX = "412 PRECONDITION_FAILED";
function isStringNull(value) {
    return value === null || value === undefined || value.trim().length === 0;
}
function fail(message) {
    throw new Error(`${EXCEPTION_MESSAGE_PREFIX} ${message}`);
}
// ---- Byte helpers (no Buffer, so the same code runs in browsers and mobile engines) ----
/** UTF-8 encode a string to bytes. */
function utf8Encode(text) {
    const binary = unescape(encodeURIComponent(text));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}
/** UTF-8 decode bytes to a string. */
function utf8Decode(bytes) {
    return decodeURIComponent(escape(bytesToBinaryString(bytes)));
}
/** Bytes to a forge "binary string" (one character per byte, 0 to 255). */
function bytesToBinaryString(bytes) {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return binary;
}
/** A forge "binary string" back to bytes. */
function binaryStringToBytes(binary) {
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i) & 0xff;
    }
    return bytes;
}
function hexEncode(bytes) {
    let hex = "";
    for (let i = 0; i < bytes.length; i++) {
        hex += (bytes[i] < 16 ? "0" : "") + bytes[i].toString(16);
    }
    return hex;
}
/** Strict hex decode (Java Hex.decodeHex refuses bad input too). */
function hexDecode(hex) {
    if (hex.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(hex)) {
        fail("dataToDecrypt is not valid hex");
    }
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
    }
    return bytes;
}
function concatBytes(a, b) {
    const result = new Uint8Array(a.length + b.length);
    result.set(a, 0);
    result.set(b, a.length);
    return result;
}
/** Removes PKCS#1 v1.5 block type 1 padding: 0x00 0x01 [0xFF x at least 8] 0x00 [message]. Strict. */
function removePkcs1BlockType1Padding(block) {
    if (block.length < 11 || block[0] !== 0x00 || block[1] !== 0x01) {
        fail("response is invalid (padding header)");
    }
    let i = 2;
    while (i < block.length && block[i] === 0xff) {
        i++;
    }
    if (i - 2 < 8 || i >= block.length || block[i] !== 0x00) {
        fail("response is invalid (padding)");
    }
    return block.slice(i + 1);
}
export class CryptoBridge {
    /**
     * @param base64EncodedKey the AMOS public key, Base64 of the X.509 SubjectPublicKeyInfo DER (as Java's
     *   PublicKey.getEncoded()).
     * @param encodedKeyType must be "PUBLIC_KEY". SECRET_KEY and PRIVATE_KEY are refused.
     */
    constructor(base64EncodedKey, encodedKeyType) {
        if (isStringNull(base64EncodedKey)) {
            fail("base64EncodedKey is mandatory");
        }
        if (isStringNull(encodedKeyType)) {
            fail("encodedKeyType is mandatory");
        }
        if (encodedKeyType.toUpperCase() !== SECURITY_KEY_TYPE_PUBLIC) {
            fail("encodedKeyType is invalid");
        }
        this.keyType = SECURITY_KEY_TYPE_PUBLIC;
        let key;
        try {
            key = forge.pki.publicKeyFromAsn1(forge.asn1.fromDer(forge.util.decode64(base64EncodedKey)));
        }
        catch (e) {
            fail("base64EncodedKey is not a valid public key");
        }
        this.publicKey = key;
    }
    /** Encrypts a request payload with the AMOS public key. Returns hex. */
    encryptData(dataToEncrypt) {
        if (isStringNull(dataToEncrypt)) {
            fail("dataToEncrypt is mandatory");
        }
        const keySizeBytes = this.resolveRsaKeySizeBytes();
        // PKCS#1 v1.5 padding takes 11 bytes, so each block carries up to (key size - 11) bytes of plaintext.
        const blockSize = keySizeBytes - 11;
        const plaintext = utf8Encode(dataToEncrypt);
        let output = new Uint8Array(0);
        for (let start = 0; start < plaintext.length; start += blockSize) {
            const block = plaintext.slice(start, Math.min(start + blockSize, plaintext.length));
            const encrypted = this.publicKey.encrypt(bytesToBinaryString(block), "RSAES-PKCS1-V1_5");
            output = concatBytes(output, binaryStringToBytes(encrypted));
        }
        return hexEncode(output);
    }
    /** Decrypts a response payload (hex) that AMOS encrypted with its private key. Returns the text. */
    decryptData(dataToDecrypt) {
        if (isStringNull(dataToDecrypt)) {
            fail("dataToDecrypt is mandatory");
        }
        const keySizeBytes = this.resolveRsaKeySizeBytes();
        const ciphertext = hexDecode(dataToDecrypt);
        if (ciphertext.length % keySizeBytes !== 0) {
            fail("dataToDecrypt length does not match the key size (wrong key or corrupted data)");
        }
        let output = new Uint8Array(0);
        for (let start = 0; start < ciphertext.length; start += keySizeBytes) {
            const block = ciphertext.slice(start, start + keySizeBytes);
            const c = new forge.jsbn.BigInteger(hexEncode(block), 16);
            const m = c.modPow(this.publicKey.e, this.publicKey.n);
            output = concatBytes(output, removePkcs1BlockType1Padding(this.toFixedBytes(m, keySizeBytes)));
        }
        return utf8Decode(output);
    }
    toFixedBytes(value, byteLength) {
        let hex = value.toString(16);
        if (hex.length > byteLength * 2) {
            fail("response is invalid (block too large)");
        }
        while (hex.length < byteLength * 2) {
            hex = "0" + hex;
        }
        return hexDecode(hex);
    }
    /** RSA key size in bytes, from the loaded key's own modulus (works for 1024-bit, 2048-bit or other keys). */
    resolveRsaKeySizeBytes() {
        return Math.ceil(this.publicKey.n.bitLength() / 8);
    }
}
export default CryptoBridge;
